import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AutoComplete from 'components/field/AutoComplete';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import Form from 'components/form/Form';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import useUpdateEffect from 'hooks/useUpdateEffect';

import InternationalProforma from 'pages/Shipments/PrintOrders/InternationalProforma';
import PrintAWB from 'pages/Shipments/PrintOrders/PrintAWB';
import ChargeAccountModal from 'pages/Topbar/ChargeAccount/ChargeAccountModal';

import axios from 'services/axios';
import fetchRemainingAmount from 'services/common/fetchRemainingAmount';
import { ENUMS, ORDERS, ORDER_ACTIONS, PRICE_CONFIGURATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { store } from 'utils/configureStore';
import PATHS, { mergePaths } from 'utils/paths';

import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';

import ActionColumn from './Components/ActionColumn';
import BoxInput from './Components/BoxInput';
import PriceColumn from './Components/PriceColumn';
import ResultColumn from './Components/ResultColumn';
import TotalPrice from './Components/TotalPrice';
import * as S from './ShippingOptions.styled';
import { addShipmentResult } from './shipmentResultsSlice';

const CreateShipmentBulkAggregatorForm = (props, ref) => {
	const {
		ids,
		isReturnShipment,
		setIsAllShipmentsCreated,
		setLoading,
		setSuccessCount,
		setFailedCount,
		setTotalCount,
		setErrorCount
	} = props;

	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { showError, showSuccess, showInfo } = useAlert();
	const { closeModal } = useModalRouter();
	const { updateOnboarding } = useAppTracking();
	const orders = useRef(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isAnyShipmentCreated, setIsAnyShipmentCreated] = useState(false);
	const [isAllCreated, setIsAllCreated] = useState(false);
	const [isAsyncCreateWorking, setIsAsyncCreateWorking] = useState(false);
	const shipmentResults = useSelector((state) => state.shipmentResults);
	const defaultBoxState = useSelector((state) => state.user.defaultBox);
	const defaultBox = defaultBoxState
		? {
				ID: defaultBoxState.id,
				width: defaultBoxState.width,
				height: defaultBoxState.height,
				length: defaultBoxState.length,
				name: defaultBoxState.boxName,
				weight: 1
			}
		: { width: 10, height: 10, length: 10, weight: 1 };

	const [lastIndexOfErrorLogs, setLastIndexOfErrorLogs] = useState(null); // To retrieve error logs for shipment creation when 5 or more shipments are trying to create simultaneously.

	const schema = useMemo(
		() =>
			yup.object().shape({
				items: yup.array().of(
					yup.object().shape({
						orderID: yup.string().nullable().required(),
						manualShipment: yup.boolean(),
						dcSettingsID: yup.mixed().nullable().required(),
						packageWeight: yup.number(),
						price: yup.number().positive().required(),
						boxes: yup
							.array()
							.of(
								yup.object().shape({
									ID: yup.number().nullable(),
									width: yup.number().positive().nullable().required(),
									height: yup.number().positive().nullable().required(),
									length: yup.number().positive().nullable().required(),
									weight: yup.number().nullable()
								})
							)
							.test({
								message: 'error.boxCountMustBeGreaterThanZero',
								test: (arr) => arr.length > 0
							})
					})
				)
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema)
	});

	const {
		handleSubmit,
		setValue,
		reset,
		watch,
		getValues,
		formState: { isSubmitting },
		control
	} = formMethods;

	const watchedItems = useWatch({
		control,
		name: 'items'
	});

	useEffect(() => {
		setTotalCount?.(watchedItems?.length ?? 0);
		setErrorCount?.(watchedItems?.filter((item) => item?.needToVerifyCrDocStatus || item?.error)?.length ?? 0);
	}, [watchedItems]);

	const shouldShowNotifyField = useMemo(
		() => watchedItems?.some((item) => item?.notice || item?.needToVerifyCrDocStatus || item?.error),
		[watchedItems]
	);

	useEffect(() => {
		setLoading?.(isLoading || isSubmitting || isAsyncCreateWorking);
	}, [isLoading, isSubmitting, isAsyncCreateWorking]);

	useUpdateEffect(() => {
		const tempOrders = [...orders.current];
		let successCount = 0;
		let failedCount = 0;
		const totalCount = tempOrders.length;

		orders.current?.forEach((order, i) => {
			const result = shipmentResults[order.otoID];

			if (result) {
				if (result.shipmentNumber) {
					tempOrders[i].shipmentID = result.shipmentNumber;
					setValue(`items[${i}].shipmentID`, result.shipmentNumber);
					successCount += 1;
				} else if (result.error) {
					tempOrders[i].error = result.error;
					failedCount += 1;
				}
			}
		});

		if (successCount > 0 || failedCount > 0) {
			setIsAnyShipmentCreated(true);
		}

		if (successCount + failedCount === totalCount) {
			setIsAsyncCreateWorking(false);
			dispatch(updateDynamicListsInPage());
			fetchRemainingAmount();
		}

		if (successCount === totalCount) {
			setIsAllCreated(true);
			setIsAllShipmentsCreated?.(true);
		}

		orders.current = tempOrders;
		setSuccessCount?.(successCount);
		setFailedCount?.(failedCount);
		setTotalCount?.(totalCount);
	}, [shipmentResults]);

	const fetchOrder = async () => {
		setIsLoading(true);

		await axios
			.post(ORDERS.GET_ORDER_SUMMARY_BY_ID_V2, {
				orderIDs: ids?.map((id) => Number(id)),
				isReturn: isReturnShipment,
				lastIndexOfErrorLogs: lastIndexOfErrorLogs ?? undefined
			})
			.then((response) => {
				if (response?.status === 200) {
					const orderDetails = response.data?.orderDetails;

					if (!orderDetails || orderDetails.length === 0) {
						reset({
							items: []
						});
						showError();
						return;
					}

					orderDetails.forEach((order) => {
						if (order.error || order.shipmentNumber) {
							dispatch(
								addShipmentResult({
									[order.otoID]: {
										shipmentNumber: order.shipmentNumber,
										dcSettingsID: order.dcSettingsID ? Number(order.dcSettingsID) : undefined,
										error: order.error
									}
								})
							);
						}
					});

					orders.current = orderDetails.map((order) => ({
						...order,
						shipmentID: order.shipmentNumber ? order.shipmentNumber : undefined
					}));

					reset({
						items: orderDetails.map((order) => ({
							ID: order.otoID,
							orderID: order.orderID,
							manualShipment: true,
							dcSettingsID: order.shipmentNumber ? Number(order.dcSettingsID) : null,
							packageWeight: order.weight !== undefined ? order.weight : 1,
							totalDue: order.totalDue,
							boxes:
								order.boxes ??
								[...Array(order?.packageCount).keys()]?.map((_, i) => ({
									...defaultBox,
									...(order?.weight > 0
										? i === 0
											? {
													weight: Number(
														(
															parseInt((order?.weight * 10) / order?.packageCount, 10) /
																10 +
															((order?.weight * 10) % order?.packageCount) / 10
														).toFixed(1)
													)
												}
											: {
													weight: Number(
														(
															parseInt((order?.weight * 10) / order?.packageCount, 10) /
															10
														).toFixed(1)
													)
												}
										: {})
								})),
							price: order.shipmentNumber ? order.shippingAmount : undefined,
							shipmentID: order.shipmentNumber ? order.shipmentNumber : undefined
						}))
					});
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});

		setIsLoading(false);
	};

	useEffect(() => {
		if (ids?.length > 0) {
			fetchOrder();
		} else {
			showError({
				header: 'error',
				message: 'alert.orderNotSelected'
			});
			closeModal();
		}
	}, []);

	// if async create is working and does not complete in 15 seconds then try to fetch order again
	useUpdateEffect(() => {
		let intervalId = null;

		if (isAsyncCreateWorking) {
			intervalId = setInterval(() => {
				fetchOrder();
			}, 15000); // 15 seconds
		}

		return () => {
			clearInterval(intervalId);
		};
	}, [isAsyncCreateWorking]);

	const handleCreateShipment = async (formData) => {
		const user = store.getState().user;
		const isTrUser = user.localization.countryISO2 === 'TR';
		if (isTrUser && !user?.nationalIDVerified && user.planName !== 'EnterprisePackage') {
			showError({
				message: 'youCanVerifyYourID',
				link: 'goToProfile',
				linkAction: () => {
					navigate(PATHS.USER_SETTINGS);
				}
			});
			return;
		}
		const mobileVerified = user.companyAdminVerified;
		if (!mobileVerified) {
			showError({
				message: 'errorMsg.verifyMobileNumber',
				link: 'verifyMobileNumberButton',
				linkAction: () => {
					closeModal();
					navigate(PATHS.USER_SETTINGS);
				}
			});
			return;
		}

		await axios
			.post(ORDER_ACTIONS.CREATE_SHIPMENT, {
				items: formData.items
					.filter((item) => !item.shipmentID)
					.map((item) => ({
						...item,
						orderID: item.ID,
						packageWeight: Number(
							item.boxes.reduce((acc, curr) => Number(acc) + Number(curr.weight), 0).toFixed(1)
						)
					})),
				reverseShipment: isReturnShipment
			})
			.then((response) => {
				const { status, data } = response;

				if (status === 200) {
					if (!store.getState().instruction.codShipmentCheck) {
						formData.items.some((element) => {
							if (element.totalDue > 0) {
								updateOnboarding(['codShipmentCheck']);
								return true;
							}
						});
					}

					if (data?.data?.type === 'async') {
						setLastIndexOfErrorLogs(data?.lastIndexOfErrorLogs);
						showInfo({
							header: 'asyncShipmentCreated',
							message: 'asyncShipmentCreatedMessage'
						});
						setIsAsyncCreateWorking(true);
					} else {
						const shipments = data?.data?.shipments;
						const hasErrorArr = [];
						const hasShipmentArr = [];
						shipments?.forEach(({ id, shipmentId, dcSettingsID, error }) => {
							if (error) {
								dispatch(
									addShipmentResult({
										[id]: {
											error
										}
									})
								);

								hasErrorArr.push(formData.items.find((item) => String(item.ID) === String(id)).ID);
							}

							if (shipmentId) {
								dispatch(
									addShipmentResult({
										[id]: {
											shipmentNumber: shipmentId,
											dcSettingsID: Number(dcSettingsID)
										}
									})
								);

								hasShipmentArr.push(formData.items.find((item) => String(item.ID) === String(id)).ID);
							}
						});

						if (hasErrorArr.length > 0) {
							showError({
								header: 'errorMsg.shipmentFailedForTheseOrders',
								persist: true,
								message: hasErrorArr.join(', '),
								link: 'shipmentErrorLogs',
								linkAction: () => {
									window.open(mergePaths(PATHS.SYSTEM_LOGS, PATHS.SHIPMENT_ERROR_LOGS));
								}
							});

							if (hasShipmentArr.length > 0) {
								showSuccess({
									header: 'successMsg.shipmentSuccessForTheseOrders',
									message: hasShipmentArr.join(', ')
								});
							}
						} else {
							showSuccess();
						}
					}
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				if (error.response?.data?.code === 'error.O004') {
					showError({
						header: 'error',
						message: 'error.O004',
						link: 'shipmentErrorLogs',
						linkAction: () => {
							window.open(mergePaths(PATHS.SYSTEM_LOGS, PATHS.SHIPMENT_ERROR_LOGS));
						}
					});
				} else if (error.response?.data?.code === 'error.O005') {
					showError({
						header: 'error',
						message: 'error.O005'
					});
					NiceModal.show(ChargeAccountModal).then(async () => {
						const totalPrice = getValues('items')?.reduce(
							(acc, item) => acc + (item.shipmentID ? 0 : (item.price ?? 0)),
							0
						);
						const { remainingAmount } = await fetchRemainingAmount();
						if (remainingAmount >= totalPrice) {
							handleCreateShipment(formData);
						} else {
							showError({
								header: 'priceCalculator.notEnoughCreditHeader',
								message: 'priceCalculator.notEnoughCreditMessage'
							});
						}
					});
				} else if (error.response?.data.code === 'error.O022') {
					showError({
						message: 'error.orderActionNotPermittedForUser'
					});
				} else {
					AppLogger.error(error);
					showError();
				}
			});
	};

	const onSubmit = async (data) => {
		const user = store.getState().user;
		const userCredit = user.remainingAmount;

		const totalPrice = getValues('items')?.reduce(
			(acc, item) => acc + (item.shipmentID ? 0 : (item.price ?? 0)),
			0
		);

		if (userCredit < totalPrice) {
			const { remainingAmount } = await fetchRemainingAmount();
			if (remainingAmount >= totalPrice) {
				await handleCreateShipment(data);
				return;
			}
			await NiceModal.show(ChargeAccountModal).then(async () => {
				const { remainingAmount } = await fetchRemainingAmount();
				if (remainingAmount >= totalPrice) {
					handleCreateShipment(data);
				} else {
					showError({
						header: 'priceCalculator.notEnoughCreditHeader',
						message: 'priceCalculator.notEnoughCreditMessage'
					});
				}
			});
		} else {
			await handleCreateShipment(data);
		}
	};

	const onError = (errors) => {
		AppLogger.log(errors);
		showError({
			header: 'error',
			message: 'alert.checkOrders'
		});
	};

	useImperativeHandle(
		ref,
		() => ({
			async handleCreateShipment() {
				await handleSubmit(onSubmit, onError)();
			},
			async handlePrintAWB() {
				NiceModal.show(PrintAWB, {
					ids: orders.current.map((order) => order.otoID),
					reverse: isReturnShipment
				}).then((resolved) => {
					const { internationalOrders = null } = resolved;

					if (internationalOrders?.length > 0) {
						NiceModal.show(InternationalProforma, {
							ids: internationalOrders
						});
					}
				});
			}
		}),
		[]
	);

	const fetchSingleOtoDeliveryFee = useCallback(async (data, fieldName) => {
		setValue(`${fieldName}.priceLoading`, true);

		await axios
			.post(PRICE_CONFIGURATIONS.GET_PRICE_LIST, {
				otoId: data.ID,
				deliveryCompanySettingsId: data.dcSettingsID,
				isReverseShipment: isReturnShipment,
				calculationData: {
					boxes: data.boxes
				}
			})
			.then((response) => {
				if (response?.status === 200) {
					const price = response.data?.data?.[0]?.price;
					if (price) {
						setValue(`${fieldName}.price`, response.data?.data?.[0]?.price, {
							shouldValidate: true,
							shouldDirty: true
						});
					} else {
						setValue(`${fieldName}.price`, null, {
							shouldValidate: true,
							shouldDirty: true
						});
						setValue(`${fieldName}.priceOptionId`, null);
						setValue(`${fieldName}.notice`, null);
						setValue(`${fieldName}.needToVerifyCrDocStatus`, false);
						setValue(`${fieldName}.deliveryType`, null);
						setValue(`${fieldName}.priceLoading`, false);
						setValue(`${fieldName}.dcSettingsID`, null, {
							shouldValidate: true,
							shouldDirty: true
						});
						setValue(`${fieldName}.pickingType`, null, {
							shouldValidate: true,
							shouldDirty: true
						});
						setValue(`${fieldName}.pickupDropoff`, null, {
							shouldValidate: true,
							shouldDirty: true
						});
					}
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
			});

		setValue(`${fieldName}.priceLoading`, false);
	}, []);

	const columns = useMemo(
		() => [
			{
				header: 'orderID',
				accessorKey: 'orderID',
				size: 80,
				showOnMobile: true
			},
			{
				header: 'destination',
				accessorKey: isReturnShipment ? 'originCity' : 'destinationCity',
				size: 80
			},
			{
				id: 'deliveryCompany',
				header: ({ table }) =>
					table.options?.data?.length <= 1 || table.options.meta.isLoading ? (
						<Typography fontWeight="700">selectDeliveryCompany</Typography>
					) : table.options.meta.isAllCreated ||
					  table.options.meta.isSubmitting ||
					  table.options.meta.isAsyncCreateWorking ? (
						<Typography fontWeight="700">deliveryCompany</Typography>
					) : (
						<DeliveryCompanyHeader />
					),
				renderCell: ({ row, table }) => (
					<DeliveryCompanyCell
						row={row}
						isReturnShipment={isReturnShipment}
						isSubmitting={table.options.meta.isSubmitting || table.options.meta.isAsyncCreateWorking}
					/>
				),
				size: 120,
				showOnMobile: true
			},
			{
				id: 'notify',
				header: '',
				renderCell: ({ row }) => {
					const notice = watch(`items[${row.index}].notice`);
					const error = watch(`items[${row.index}].error`);
					const verifyError = watch(`items[${row.index}].needToVerifyCrDocStatus`);
					return (
						<S.NotifyCell>
							{notice && (
								<Tooltip title={<NoticeCell data={notice?.split(',')} />} placement="top">
									<Icon size="large" icon={faTriangleExclamation} color="var(--yellow)" />
								</Tooltip>
							)}

							{verifyError && (
								<Tooltip title="otoRatesVerifyError" placement="top">
									<Icon size="large" icon={faTriangleExclamation} color="var(--red)" />
								</Tooltip>
							)}

							{error && (
								<Tooltip title={`price-calculator.${error}.title`} placement="top">
									<Icon size="large" icon={faTriangleExclamation} color="var(--red)" />
								</Tooltip>
							)}
						</S.NotifyCell>
					);
				},
				size: 60
			},

			{
				id: 'shipmentNumber',
				header: 'shipmentNumber',
				renderCell: ({ row, table }) => (
					<ResultColumn
						data={row.original}
						isSubmitting={table.options.meta.isSubmitting || table.options.meta.isAsyncCreateWorking}
					/>
				),
				size: 150,
				maxSize: 150
			},
			{
				header: 'pickupType',
				renderCell: ({ row, table }) => (
					<EnumSelect
						name={`items[${row.index}].pickingType`}
						enumName={ENUMS.PRICE_CONFIGURATION_PICKUP_DROPOFF}
						value={watch(`items[${row.index}].pickingType`)}
						setValue={(value) => {
							setValue(`items[${row.index}].pickingType`, value);
						}}
						disabled={
							!(
								!!row.original.shipmentID ||
								table.options.meta.isSubmitting ||
								table.options.meta.isAsyncCreateWorking
							) &&
							(!watch(`items[${row.index}].dcSettingsID`) ||
								watch(`items[${row.index}].pickupDropoff`) !== 'PICKUP_BY_DC_OR_BRANCH_DROP_OF')
						}
						viewMode={
							!!row.original.shipmentID ||
							table.options.meta.isSubmitting ||
							table.options.meta.isAsyncCreateWorking
						}
						searchable={false}
						valueRemovable={false}
					/>
				),
				size: 120,
				showOnMobile: false
			},
			{
				header: 'deliveryType',
				renderCell: ({ row, table }) => (
					<EnumSelect
						name={`items[${row.index}].deliveryType`}
						enumName={ENUMS.PRICE_CONFIGURATION_DELIVERY_TYPE}
						value={watch(`items[${row.index}].deliveryType`)}
						setValue={(value) => {
							setValue(`items[${row.index}].deliveryType`, value);
						}}
						disabled={
							!row.original.shipmentID &&
							!table.options.meta.isSubmitting &&
							!table.options.meta.isAsyncCreateWorking
						}
						viewMode={
							!!row.original.shipmentID ||
							table.options.meta.isSubmitting ||
							table.options.meta.isAsyncCreateWorking
						}
						searchable={false}
						valueRemovable={false}
					/>
				),
				size: 120,
				showOnMobile: false
			},
			{
				header: 'boxCount',
				renderCell: ({ row, table }) => (
					<BoxInput
						fieldName={`items[${row.index}]`}
						orderID={row.original.orderID}
						fetchDeliveryFee={fetchSingleOtoDeliveryFee}
						rowIndex={row.index}
						viewMode={
							!!row.original.shipmentID ||
							table.options.meta.isSubmitting ||
							table.options.meta.isAsyncCreateWorking
						}
						disabled
					/>
				),
				size: 80,
				maxSize: 80,
				showOnMobile: false
			},
			{
				header: 'boxWeight',
				accessorKey: 'weight',
				renderCell: ({ row, table }) => (
					<IntegerInput
						value={Number(
							watch(`items[${row.index}].boxes`)
								?.reduce((acc, box) => acc + box.weight, 0)
								.toFixed(1)
						)}
						unit="kg"
						disabled={
							!row.original.shipmentID &&
							!table.options.meta.isSubmitting &&
							!table.options.meta.isAsyncCreateWorking
						}
						viewMode={
							!!row.original.shipmentID ||
							table.options.meta.isSubmitting ||
							table.options.meta.isAsyncCreateWorking
						}
					/>
				),
				size: 60,
				maxSize: 60,
				showOnMobile: false
			},
			{
				header: 'price',
				renderCell: ({ row }) => <PriceColumn index={row.index} />,
				size: 50,
				maxSize: 50,
				showOnMobile: true
			},
			{
				id: 'rowAction',
				header: '',
				renderCell: ({ row, table }) => (
					<ActionColumn
						row={row}
						orders={table.options?.data}
						setOrders={(data) => {
							orders.current = data;
						}}
						isReturnShipment={isReturnShipment}
						isSubmitting={table.options.meta.isSubmitting || table.options.meta.isAsyncCreateWorking}
					/>
				),
				size: 40,
				maxSize: 40,
				showOnMobile: true
			}
		],
		[]
	);

	const visibleColumns = useMemo(
		() => ({
			shipmentNumber: isAnyShipmentCreated,
			notify: shouldShowNotifyField
		}),
		[isAnyShipmentCreated, shouldShowNotifyField]
	);

	const [metaData, setMetaData] = useState({
		isAllCreated,
		isSubmitting,
		isAsyncCreateWorking,
		isLoading
	});

	useEffect(() => {
		setMetaData({
			isAllCreated,
			isSubmitting,
			isAsyncCreateWorking,
			isLoading
		});
	}, [isAllCreated, isSubmitting, isAsyncCreateWorking, isLoading]);

	return (
		<Form formMethods={formMethods} borderless className="mobile-no-padding">
			<Table
				data={orders.current}
				columns={columns}
				visibleColumns={visibleColumns}
				loading={isLoading}
				meta={metaData}
			/>
			{!isAllCreated && <TotalPrice />}
		</Form>
	);
};

const DeliveryCompanyCell = ({ row, isReturnShipment, isSubmitting }) => {
	const { showError } = useAlert();
	const axios = useAxiosPrivate();
	const formMethods = useFormContext();
	const { watch, getValues, setValue } = formMethods;
	const { track } = useAppTracking();

	const deliveryCompaniesColumns = useMemo(
		() => [
			{
				accessor: 'logo',
				render: (logo) => <Image url={logo} alt="logo" width="25px" height="25px" />,
				width: 50
			},
			{ accessor: 'name', width: 150 },
			{ accessor: 'price', width: 70 }
		],
		[]
	);
	const deliveryCompaniesLabels = useMemo(() => ['name'], []);

	const fetchAutocompleteData = useCallback(
		async (props) => {
			const order = getValues(`items[${row.index}]`);

			const data = await axios
				.post(PRICE_CONFIGURATIONS.GET_PRICE_LIST, {
					otoId: order.ID,
					deliveryCompanySettingsId: props?.IDs?.[0],
					isReverseShipment: isReturnShipment,
					calculationData: {
						boxes: order.boxes
					},
					blockBulletAggregator: true
				})
				.then((response) => {
					if (!response.data.success) {
						setValue(`items[${row.index}].error`, response.data.error);
						return;
					}
					track(
						'shippingPricesCheck',
						{
							pricesCount: response.data.size,
							originCity: row.original.originCity,
							destinationCity: row.original.destinationCity,
							originCountry: '',
							destinationCountry: '',
							shipmentWeight: order.boxes[0]?.weight,
							boxType: order.boxes[0]?.name,
							volumetricWeight: response.data.volumetricWeight
						},
						['mixpanel']
					);
					if (response.data.size === 0) {
						setValue(`items[${row.index}].price`, null, {
							shouldValidate: true,
							shouldDirty: true
						});
						setValue(`items[${row.index}].priceOptionId`, null);
						setValue(`items[${row.index}].notice`, null);
						setValue(`items[${row.index}].deliveryType`, null);
						setValue(`items[${row.index}].needToVerifyCrDocStatus`, false);
						setValue(`items[${row.index}].priceLoading`, false);
						setValue(`items[${row.index}].dcSettingsID`, null, {
							shouldValidate: true,
							shouldDirty: true
						});
						setValue(`items[${row.index}].pickingType`, null, {
							shouldValidate: true,
							shouldDirty: true
						});
						setValue(`items[${row.index}].pickupDropoff`, null, {
							shouldValidate: true,
							shouldDirty: true
						});
					}
					return response.data.data.map((priceObj) => ({
						ID: priceObj.deliveryCompanySettingsId,
						name: priceObj.nameAr,
						priceOptionID: priceObj.otoDeliveryPriceId,
						notice: priceObj.notice,
						needToVerifyCrDocStatus: priceObj.needToVerifyCrDocStatus,
						price: priceObj.price,
						logo: priceObj.logo,
						pickupDropoff: priceObj.pickupType,
						deliveryType: priceObj.deliveryType
					}));
				})
				.catch((error) => {
					showError();
					AppLogger.error(error);
				});

			return data.filter((item) =>
				props?.keyword
					? item.name.toLowerCase().includes(props?.keyword.toLowerCase())
					: props?.IDs
						? props?.IDs.includes(item.ID)
						: true
			);
		},
		[isReturnShipment]
	);

	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'flex-end'
			}}>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					width: '100%'
				}}>
				<AutoComplete
					columns={deliveryCompaniesColumns}
					setValue={(value, isFirstRender) => {
						if (isFirstRender) return;

						setValue(`items[${row.index}].price`, value?.ID ? value.price : null, {
							shouldValidate: true,
							shouldDirty: true
						});

						setValue(`items[${row.index}].priceOptionId`, value?.priceOptionID ?? null);

						setValue(`items[${row.index}].notice`, value?.notice ?? null);

						setValue(`items[${row.index}].needToVerifyCrDocStatus`, value?.needToVerifyCrDocStatus ?? null);

						setValue(`items[${row.index}].deliveryType`, value?.deliveryType ?? null);

						setValue(`items[${row.index}].priceLoading`, false);

						setValue(`items[${row.index}].dcSettingsID`, value?.ID ?? null, {
							shouldValidate: true,
							shouldDirty: true
						});

						const pickupDropoff = value?.pickupDropoff ?? null;

						if (pickupDropoff === 'PICKUP_BY_DC_OR_BRANCH_DROP_OF' || pickupDropoff === 'PICKUP_BY_DC') {
							setValue(`items[${row.index}].pickingType`, 'PICKUP_BY_DC', {
								shouldValidate: true,
								shouldDirty: true
							});
						} else if (pickupDropoff === 'BRANCH_DROP_OFF') {
							setValue(`items[${row.index}].pickingType`, 'BRANCH_DROP_OFF', {
								shouldValidate: true,
								shouldDirty: true
							});
						}

						setValue(`items[${row.index}].pickupDropoff`, value?.pickupDropoff ?? null, {
							shouldValidate: true,
							shouldDirty: true
						});
					}}
					value={watch(`items[${row.index}].dcSettingsID`)}
					selectionLabelAccessor={deliveryCompaniesLabels}
					valueRemovable={false}
					fetchFunction={fetchAutocompleteData}
					name={`items[${row.index}].dcSettingsID`}
					viewMode={!!row.original.shipmentID || isSubmitting}
					fixedContainerWidth={false}
				/>
			</div>
		</div>
	);
};

const DeliveryCompanyHeader = () => {
	const { showError } = useAlert();
	const [commonDeliveryCompany, setCommonDeliveryCompany] = useState(null);
	const deliveryCompaniesColumns = useMemo(
		() => [
			{
				accessor: 'logo',
				render: (logo) => <Image url={logo} alt="logo" width="25px" height="25px" />,
				width: 35
			},
			{ accessor: 'name' }
		],
		[]
	);
	const deliveryCompaniesLabels = useMemo(() => ['name'], []);

	const formMethods = useFormContext();
	const { control, setValue } = formMethods;

	const watchedItems = useWatch({
		control,
		name: 'items'
	});

	const fetchAutocompleteData = useCallback(async (props) => {
		const data = await axios
			.post(PRICE_CONFIGURATIONS.GET_DELIVERY_COMPANY_AUTOCOMPLETE)
			.then((response) => response.data)
			.catch((error) => {
				showError();
				AppLogger.error(error);
			});

		return data.filter((item) =>
			props?.keyword
				? item.name.toLowerCase().includes(props?.keyword.toLowerCase())
				: props?.IDs
					? props?.IDs.includes(item.ID)
					: true
		);
	}, []);

	return (
		<AutoComplete
			columns={deliveryCompaniesColumns}
			setValue={(value) => {
				setCommonDeliveryCompany(value?.ID ?? null);

				watchedItems.forEach((item, index) => {
					if (item.shipmentID || item.dcSettingsID === value?.ID || !value?.ID) return;

					setValue(`items[${index}].dcSettingsID`, value?.ID ?? null, {
						shouldValidate: true,
						shouldDirty: true
					});

					setValue(`items[${index}].price`, null, {
						shouldValidate: true,
						shouldDirty: true
					});

					setValue(`items[${index}].priceLoading`, true, {
						shouldValidate: true,
						shouldDirty: true
					});
				});
			}}
			value={commonDeliveryCompany}
			placeholder="selectDeliveryCompany"
			selectionLabelAccessor={deliveryCompaniesLabels}
			fetchFunction={fetchAutocompleteData}
			fixedContainerWidth={false}
		/>
	);
};

const Ul = ({ children }) => <ul>{children}</ul>;
const Li = ({ children }) => <li>{children}</li>;

const NoticeCell = ({ data }) => {
	return (
		<S.InstructionWrapper>
			{data.map((instruction) => (
				<S.InstructionColumn key={instruction}>
					<Typography variant="p2" className="instruction-detail" noWrap color="var(--alwaysWhiteColor)">
						<Trans
							i18nKey={instruction}
							components={{
								blackText: (
									<Typography variant="p2" fontWeight="500" noWrap color="var(--alwaysWhiteColor)" />
								),
								ul: <Ul />,
								li: <Li />,
								p: <Typography variant="p2" fontWeight="500" noWrap color="var(--alwaysWhiteColor)" />,
								bold: (
									<Typography variant="p2" fontWeight="700" noWrap color="var(--alwaysWhiteColor)" />
								)
							}}
						/>
					</Typography>
				</S.InstructionColumn>
			))}
		</S.InstructionWrapper>
	);
};

export default forwardRef(CreateShipmentBulkAggregatorForm);
